import RecruitmentAsset from "./RecruitmentAsset";
import staticQuestionsIllustration from "../assets/ScheduleInterviewAssets/questionsimg.svg";
import "./RecruitmentForms.css";
//template form with generate skills
import React, { useState, useEffect } from "react";
import { authFetch } from "../services/httpClient";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Tooltip,
  InputAdornment
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Code as CodeIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import TechStackModal from "./TechStackModal";
import { createTemplate, updateTemplate, getTemplate, uploadJobDescription, getCodeEvaluationLanguages, getJobDivaJobDetail, scrapeJobFromUrl, generateQuestionBank } from "../services/api";
import { getInterviewMode } from "../services/settingsapi";
import { useNotification } from "../../interviewAI/NotificationContext";

const difficultyTagStyle = (difficulty) => {
  const value = String(difficulty || "").toLowerCase();
  if (value === "advanced" || value === "high") return { backgroundColor: "#FFF0F0", color: "#D32F2F", border: "1px solid #D32F2F" };
  if (value === "intermediate" || value === "medium") return { backgroundColor: "#FFF3D8", color: "#A86600", border: "1px solid #A86600" };
  return { backgroundColor: "#E2F8EC", color: "#008E53", border: "1px solid #008E53" };
};


const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "var(--shadow)",
  backgroundColor: "var(--bg-card)",
  border: "1px solid var(--border-color)",
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: `2px solid var(--primary)`,
  display: "inline-block",
  color: "var(--text-primary)",
  fontWeight: 800,
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UploadBox = styled(Box)(({ theme }) => ({
  border: `2px dashed var(--primary-light)`,
  borderRadius: "10px",
  padding: theme.spacing(2),
  backgroundColor: "var(--bg-input)",
  color: "var(--text-primary)",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: "var(--primary)",
    backgroundColor: "var(--primary-light)",
  },
}));

const TemplateForm = ({ onSuccess, onCancel }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isClone = Boolean(location?.pathname?.includes('/clone')) || Boolean(location.state && location.state.clone);
  const isEdit = Boolean(id) && !isClone;
  const { notify } = useNotification();

  const [formData, setFormData] = useState({
    // reqId: "",
    name: "",
    jd: "",
    techStackData: [],
    interviewDuration: "",
    resumeEvaluation: false,
    resumeEvaluationTimePercentage: 0,
    staticQuestionsEnabled: false,
    staticQuestions: [],
    codeEvaluation: false,
    codingLanguages: [], // Array of { language, complexity }
    plannedInterview: null,
    interviewModeOverride: "GLOBAL",
  });

  const [techInput, setTechInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [openTechEditor, setOpenTechEditor] = useState(false);
  const [openStaticDialog, setOpenStaticDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");

  // --- Code Evaluation States ---
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedComplexity, setSelectedComplexity] = useState("Intermediate");

  // --- States for Job Description File Upload ---
  const [jdUploading, setJdUploading] = useState(false);
  const [jdFileName, setJdFileName] = useState("");
  const [jdPreview, setJdPreview] = useState("");

  const [jobUrl, setJobUrl] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);

  const [jobDivaRef, setJobDivaRef] = useState("");
  const [jobDivaLoading, setJobDivaLoading] = useState(false);

  const [reqId, setReqId] = useState("");
  const [pageError, setPageError] = useState("");

  const [isGeneratingJD, setIsGeneratingJD] = useState(false);
  const [questionBankStatus, setQuestionBankStatus] = useState(null);
  const [questionBankLoading, setQuestionBankLoading] = useState(false);
  const [savedQuestionBankConfig, setSavedQuestionBankConfig] = useState(null);
  const [globalInterviewMode, setGlobalInterviewMode] = useState(null);

  const clampAllocation = (value, maximum = 100) =>
    Math.min(Math.max(0, Number.parseInt(value, 10) || 0), Math.max(0, maximum));

  // Split an available budget using whole percentages, including the remainder.
  // Keeping this in one place prevents generated/imported skills from ever
  // creating an allocation above the remaining evaluation budget.
  const allocateTechBudget = (skills, budget) => {
    const safeSkills = skills.filter((skill) => skill?.name);
    if (!safeSkills.length) return [];
    const available = clampAllocation(budget);
    const base = Math.floor(available / safeSkills.length);
    const remainder = available % safeSkills.length;
    return safeSkills.map((skill, index) => ({
      ...skill,
      percentage: base + (index < remainder ? 1 : 0),
      difficulty: skill.difficulty || "Intermediate",
    }));
  };

  const normalizeAllocations = (techs, resumeEnabled, resumeValue) => {
    const resume = resumeEnabled ? clampAllocation(resumeValue) : 0;
    let remaining = 100 - resume;
    const safeTechs = (techs || []).map((tech) => {
      const percentage = clampAllocation(tech.percentage, remaining);
      remaining -= percentage;
      return { ...tech, percentage };
    });
    return { techs: safeTechs, resume };
  };

  const isPlannedInterview =
    formData.interviewModeOverride === "PLANNED"
      ? true
      : formData.interviewModeOverride === "DYNAMIC"
        ? false
        : formData.plannedInterview === true;

  const displayedInterviewMode =
    formData.interviewModeOverride === "PLANNED"
      ? "PLANNED"
      : formData.interviewModeOverride === "DYNAMIC"
        ? "DYNAMIC"
        : globalInterviewMode === "planned"
          ? "PLANNED"
          : globalInterviewMode === "dynamic"
            ? "DYNAMIC"
            : "";
  const questionBankConfig = JSON.stringify({
    interviewDuration: formData.interviewDuration,
    techStackData: formData.techStackData,
    plannedInterview: formData.plannedInterview,
    interviewModeOverride: formData.interviewModeOverride,
  });
  const questionBankOutdated = Boolean(String(questionBankStatus || "").toUpperCase() === "READY" && savedQuestionBankConfig && savedQuestionBankConfig !== questionBankConfig);

  // Fetch languages on component mount
  useEffect(() => {
    const fetchLanguages = async () => {
      setLanguagesLoading(true);
      try {
        const langs = await getCodeEvaluationLanguages();
        setLanguages(langs);
      } catch (error) {
        console.error("Error fetching languages:", error);
        setLanguages([]);
      } finally {
        setLanguagesLoading(false);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getInterviewMode()
      .then((data) => {
        if (!cancelled) {
          const mode = data?.mode === "planned" ? "planned" : "dynamic";
          setGlobalInterviewMode(mode);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error loading global interview mode:", error);
          setGlobalInterviewMode("dynamic");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (id) {
      fetchTemplate();
    }
  }, [id, isClone]);

  const fetchTemplate = async () => {
    try {
      const template = await getTemplate(id);
      const techStackWithDefaults = (template.techStackData || []).map(tech => ({
        name: tech.name,
        percentage: tech.percentage,
        difficulty: tech.difficulty || "Intermediate", // Ensure difficulty field exists
      }));
      const safeAllocation = normalizeAllocations(
        techStackWithDefaults,
        Boolean(template.resumeEvaluation),
        template.resumeEvaluationTimePercentage || 0
      );

      // Parse coding languages - handle both array format and legacy string format
      let codingLanguages = [];
      if (template.codingLanguages) {
        if (Array.isArray(template.codingLanguages)) {
          codingLanguages = template.codingLanguages;
        } else if (typeof template.codingLanguages === "string") {
          // Parse legacy format if needed
          try {
            codingLanguages = JSON.parse(template.codingLanguages);
            if (!Array.isArray(codingLanguages)) codingLanguages = [];
          } catch (e) {
            codingLanguages = [];
          }
        }
      }
      setReqId(template.reqId || "");
      const interviewModeOverride = template.interviewModeOverride || (
        template.plannedInterview == null
          ? "GLOBAL"
          : template.plannedInterview
            ? "PLANNED"
            : "DYNAMIC"
      );
      setFormData({
        // reqId: template.reqId || "",
        name: template.name,
        jd: template.jd,
        techStackData: safeAllocation.techs,
        interviewDuration: template.interviewDuration || "",
        resumeEvaluation: template.resumeEvaluation || false,
        resumeEvaluationTimePercentage:
          safeAllocation.resume,
        staticQuestionsEnabled: template.staticQuestionsEnabled || false,
        staticQuestions: Array.isArray(template.staticQuestions)
          ? template.staticQuestions
          : template.staticQuestions
            ? template.staticQuestions
            : [],
        codeEvaluation: template.codeEvaluation || false,
        codingLanguages: codingLanguages,
        plannedInterview: template.plannedInterview == null
          ? null
          : Boolean(template.plannedInterview),
        interviewModeOverride,
      });
      setQuestionBankStatus(template.questionBankStatus || null);
      setSavedQuestionBankConfig(template.questionBankSignature ? JSON.stringify({
        interviewDuration: template.interviewDuration,
        techStackData: template.techStackData || [],
        plannedInterview: template.plannedInterview == null
          ? null
          : Boolean(template.plannedInterview),
        interviewModeOverride,
      }) : null);
    } catch (error) {
      const message = error?.message || "Unable to load this job opening.";
      console.error("Error fetching template:", error);
      setPageError(message);
      notify("error", message);
    }
  };

  const handleGenerateQuestionBank = async () => {
    if (!isEdit) {
      notify("info", "Save the Job Opening before generating its AI Question Bank.");
      return;
    }
    setQuestionBankLoading(true);
    try {
      await generateQuestionBank(id);
      setQuestionBankStatus("READY");
      setSavedQuestionBankConfig(questionBankConfig);
      notify("success", "AI Question Bank generated successfully.");
    } catch (error) {
      notify("error", error.message || "Failed to generate AI Question Bank.");
    } finally {
      setQuestionBankLoading(false);
    }
  };

  const baseURL = process.env.REACT_APP_REST_BASE_URL;

  const handleJdFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!validTypes.includes(file.type)) {
      notify("error", "Please upload a PDF or Word document (.pdf, .docx, .doc)");
      event.target.value = '';
      return;
    }

    setJdUploading(true);
    setJdFileName(file.name);
    setJdPreview("");

    try {
      const result = await uploadJobDescription(file);
      const extractedText = result.extracted_text || "";

      if (extractedText.trim()) {
        setFormData((prev) => ({
          ...prev,
          jd: extractedText,
        }));
        setJdPreview(extractedText);
        notify("success", `Job description extracted from ${file.name}`);
      } else {
        notify("warning", "No text could be extracted from the file");
      }
    } catch (error) {
      console.error("Error uploading job description:", error);
      notify(
        "error",
        `Failed to extract job description: ${error.message || "Unknown error"}`
      );
      setJdFileName("");
    } finally {
      setJdUploading(false);
      event.target.value = '';
    }
  };

  const handleGenerateSkills = async () => {
    if (!formData.jd) {
      notify("error", "Please upload or paste a job description first.");
      return;
    }

    try {
      setIsGenerating(true);

      const response = await authFetch(`${baseURL}/skills_extractor/extract-skills`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_description: formData.jd }), // use correct key
      });

      if (!response.ok) {
        notify("error", "Error generating skills. Please try again.");
        return;
      }

      const data = await response.json();
      const extractedSkills = data.skills || data.top_skills || [];

      if (extractedSkills.length === 0) {
        notify("info", "No skills detected in the job description.");
        return;
      }

      setFormData(prev => ({
        ...prev,
        techStackData: allocateTechBudget(
          extractedSkills.map((skill) => ({ name: skill, difficulty: "Intermediate" })),
          100 - (prev.resumeEvaluation ? prev.resumeEvaluationTimePercentage : 0)
        ),
      }));

      notify("success", "Skills generated successfully!");

    } catch (error) {
      console.error(error);
      notify("error", "Error generating skills. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFetchFromUrl = async () => {
    if (!jobUrl.trim()) {
      notify("error", "Please enter a job URL or JobDiva job ID");
      return;
    }

    try {
      setUrlLoading(true);

      const trimmedJobUrl = jobUrl.trim();
      const directJobIdMatch = trimmedJobUrl.match(/^\d+$/);
      const jobDivaJobId = directJobIdMatch
        ? trimmedJobUrl
        : (() => {
          try {
            const parsedUrl = new URL(trimmedJobUrl);
            const params = parsedUrl.searchParams;
            return params.get("jobId") || params.get("id") || null;
          } catch (error) {
            return null;
          }
        })();

      let data;
      if (jobDivaJobId) {
        data = await getJobDivaJobDetail(jobDivaJobId);
      } else {
        data = await scrapeJobFromUrl(trimmedJobUrl);
      }

      const title = data.jobTitle || data.job_title || data.title || data?.job?.title || "";
      const jd = data.jobDescription || data.job_description || data.description || data?.job?.description || "";
      const skills = data.top_skills || data.requiredSkills || data.skills || data?.job?.skills || [];

      // Fill JD and auto-fill job name if available
      setFormData((prev) => ({
        ...prev,
        name: title || prev.name,
        jd,
      }));

      // Fill Tech Stack
      if (Array.isArray(skills) && skills.length > 0) {
        const techStack = skills.map((skill) => ({
          name: typeof skill === "string" ? skill : skill.name || "",
          difficulty: "Intermediate",
        })).filter((skill) => skill.name);

        if (techStack.length > 0) {
          setFormData((prev) => ({
            ...prev,
            techStackData: allocateTechBudget(techStack, 100 - (prev.resumeEvaluation ? prev.resumeEvaluationTimePercentage : 0)),
          }));
        }
      }

      notify("success", jobDivaJobId ? "JobDiva job data imported successfully 🚀" : "Job data imported successfully 🚀");
    } catch (error) {
      console.error("Error importing job data:", error);
      notify("error", error.message || "Failed to fetch job data");
    } finally {
      setUrlLoading(false);
    }
  };

  const handleImportFromJobDiva = async () => {
    if (!jobDivaRef.trim()) {
      notify("error", "Please enter a JobDiva Reference");
      return;
    }

    try {
      setJobDivaLoading(true);

      const data = await getJobDivaJobDetail(jobDivaRef.trim());
      console.log("JobDiva full response:", data);

      // First try standardized fields, then fallback to raw response
      let title = data.jobTitle || "";
      let jd = data.jobDescription || "";
      let skillsRaw = data.skills || [];

      // If standardized fields are empty, search the raw response
      if (!title && data.raw) {
        const raw = data.raw;
        console.log("Searching raw response for title...", Object.keys(raw));
        for (const key of Object.keys(raw)) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes("title") || lowerKey.includes("jobtitle")) {
            title = raw[key];
            console.log("Found title at key:", key, "value:", title);
            break;
          }
        }
      }

      if (!jd && data.raw) {
        const raw = data.raw;
        for (const key of Object.keys(raw)) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes("description") || lowerKey.includes("jobdesc")) {
            jd = raw[key];
            console.log("Found description at key:", key, "value:", jd.substring(0, 100));
            break;
          }
        }
      }

      if (skillsRaw.length === 0 && data.raw) {
        const raw = data.raw;
        for (const key of Object.keys(raw)) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes("skill")) {
            const val = raw[key];
            if (Array.isArray(val)) {
              skillsRaw = val;
              console.log("Found skills at key:", key);
              break;
            }
          }
        }
      }

      console.log("Final extracted - title:", title, "jd length:", jd.length, "skills:", skillsRaw.length);

      // Fill JD and auto-fill job name if available
      if (title || jd) {
        setFormData((prev) => ({
          ...prev,
          ...(title && { name: title }),
          ...(jd && { jd }),
        }));
        notify("success", "Job details imported successfully 🚀");
      } else {
        notify("warning", "Could not extract job details. Check browser console for full response.");
        console.error("Failed to extract - raw response:", data);
      }

      // Fill Tech Stack if skills are available
      if (Array.isArray(skillsRaw) && skillsRaw.length > 0) {
        const techStack = skillsRaw
          .map((skill) => ({
            name: typeof skill === "string" ? skill : skill.name || skill.SKILL || skill.skill || JSON.stringify(skill),
            difficulty: "Intermediate",
          }))
          .filter((skill) => skill.name && skill.name.length > 0);

        if (techStack.length > 0) {
          setFormData((prev) => ({
            ...prev,
            techStackData: allocateTechBudget(techStack, 100 - (prev.resumeEvaluation ? prev.resumeEvaluationTimePercentage : 0)),
          }));
        }
      }

      setJobDivaRef("");
    } catch (error) {
      console.error("Error importing JobDiva job:", error);
      notify("error", error.message || "Failed to fetch JobDiva job");
    } finally {
      setJobDivaLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // ==========================================
    // Validate enabled evaluations
    // ==========================================

    // Resume Evaluation
    if (
      formData.resumeEvaluation &&
      (!formData.resumeEvaluationTimePercentage ||
        formData.resumeEvaluationTimePercentage <= 0)
    ) {
      notify(
        "error",
        "Resume Evaluation is enabled. Please add a valid evaluation percentage greater than 0."
      );
      return;
    }

    // Static Questions
    if (
      formData.staticQuestionsEnabled &&
      (!Array.isArray(formData.staticQuestions) ||
        formData.staticQuestions.filter(
          (q) => typeof q === "string" && q.trim()
        ).length === 0)
    ) {
      notify(
        "error",
        "Static Questions are enabled. Please add at least one valid question."
      );
      return;
    }

    // Code Evaluation
    if (formData.codeEvaluation) {
      if (
        !Array.isArray(formData.codingLanguages) ||
        formData.codingLanguages.length === 0
      ) {
        notify(
          "error",
          "Code Evaluation is enabled. Please add at least one programming language and level."
        );
        return;
      }

      const invalidCodingLanguage = formData.codingLanguages.some(
        (item) =>
          !item?.language?.trim() ||
          !item?.complexity?.trim()
      );

      if (invalidCodingLanguage) {
        notify(
          "error",
          "Each coding evaluation must have both a language and level."
        );
        return;
      }
    }

    // ==========================================
    // Validate total percentage
    // ==========================================

    const techTotal = formData.techStackData.reduce(
      (sum, t) => sum + (t.percentage || 0),
      0
    );

    const resumeEvalPercent =
      formData.resumeEvaluationTimePercentage || 0;

    if (techTotal + resumeEvalPercent !== 100) {
      notify(
        "error",
        "Total percentage (Tech Stack + Resume Evaluation) must be exactly 100%."
      );
      return;
    }

    setLoading(true);

    try {
      // Prepare payload - ensure all tech stack items have difficulty field
      const sanitizedTechStack = formData.techStackData.map(tech => ({
        name: tech.name,
        percentage: tech.percentage,
        difficulty: tech.difficulty || "Intermediate", // Default to Intermediate if missing
      }));

      // Prepare payload - codingLanguages should be an array of { language, complexity } objects
      const payload = {
        // reqId: formData.reqId,
        name: formData.name,
        jd: formData.jd,
        interviewDuration: formData.interviewDuration,
        resumeEvaluation: formData.resumeEvaluation,
        resumeEvaluationTimePercentage: formData.resumeEvaluationTimePercentage,
        staticQuestionsEnabled: formData.staticQuestionsEnabled,
        staticQuestions: formData.staticQuestions,
        codeEvaluation: formData.codeEvaluation,
        codingLanguages: formData.codingLanguages || [], // Array of { language, complexity }
        techStackData: sanitizedTechStack,
        templateStatus: "ACTIVE",
        interviewModeOverride: formData.interviewModeOverride || "GLOBAL",
      };

      if (isEdit) {
        await updateTemplate(id, payload);

        notify(
          "success",
          `Job opening ${reqId ? `(${reqId}) ` : ""}updated successfully!`
        );
      } else {
        const res = await createTemplate(payload);

        const createdReqId = res?.reqId || res?.data?.reqId || "";

        const generation = res?.questionBankGeneration;
        if (generation?.skipped) {
          notify("success", `Job Opening ${createdReqId ? `(${createdReqId}) ` : ""}created successfully.`);
        } else if (generation?.success) {
          notify("success", `Job Opening ${createdReqId ? `(${createdReqId}) ` : ""}created successfully. AI Question Bank generated successfully.`);
        } else {
          notify("warning", `Job Opening ${createdReqId ? `(${createdReqId}) ` : ""}created successfully. AI Question Bank generation failed. Please regenerate from Edit Job Opening.`);
        }
      }

      // Always navigate to templates list
      setTimeout(() => {
        navigate("/templates");
      }, 500);
    } catch (error) {
      console.error("Error saving template:", error);

      let errorMessage = "An error occurred while saving the job opening.";

      if (error.response) {
        // Server responded with an error status (4xx, 5xx)
        const responseData = error.response.data;
        if (responseData) {
          if (typeof responseData === "string") {
            errorMessage = responseData;
          } else if (typeof responseData.detail === "string") {
            errorMessage = responseData.detail;
          } else if (Array.isArray(responseData.detail)) {
            // FastAPI validation errors: array of { msg, loc } objects
            errorMessage = responseData.detail
              .map((d) => {
                const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
                return field ? `${field}: ${d.msg}` : d.msg;
              })
              .join("; ");
          } else if (typeof responseData.message === "string") {
            errorMessage = responseData.message;
          } else if (typeof responseData.error === "string") {
            errorMessage = responseData.error;
          }
        }
        if (errorMessage === "An error occurred while saving the job opening.") {
          errorMessage = `Request failed (${error.response.status}): ${error.response.statusText || "Unknown error"}`;
        }
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      notify("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTech = () => {
    const trimmed = techInput.trim();
    if (trimmed && !formData.techStackData.find((t) => t.name === trimmed)) {
      const totalPercentage = formData.techStackData.reduce(
        (sum, tech) => sum + tech.percentage,
        0
      );
      const remaining = 100 - totalPercentage - (formData.resumeEvaluation ? formData.resumeEvaluationTimePercentage : 0);
      const defaultPercentage = remaining > 0 ? remaining : 0;

      setFormData((prev) => ({
        ...prev,
        techStackData: [
          ...prev.techStackData,
          {
            name: trimmed,
            percentage: defaultPercentage,
            difficulty: "Intermediate",
          },
        ],
      }));
      setTechInput("");
    }
  };

  const handleGenerateJD = async () => {
    if (!formData.jd.trim()) {
      notify("error", "Please enter a job title or requirement.");
      return;
    }

    try {
      setIsGeneratingJD(true);

      const response = await authFetch(
        `${baseURL}/skills_extractor/generate-job-description`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: formData.jd,
          }),
        }
      );

      const data = await response.json();

      setFormData((prev) => ({
        ...prev,
        jd: data.job_description,
      }));

      notify("success", "Job Description generated successfully");
    } catch (error) {
      notify("error", "Failed to generate JD");
    } finally {
      setIsGeneratingJD(false);
    }
  };



  // Code Evaluation Handlers
  const handleAddLanguage = () => {
    if (!selectedLanguage) return;

    setFormData((prev) => ({
      ...prev,
      codingLanguages: [
        ...prev.codingLanguages,
        { language: selectedLanguage.toLowerCase(), complexity: selectedComplexity },
      ],
    }));
    setSelectedLanguage("");
    setSelectedComplexity("Intermediate");
  };

  const handleRemoveLanguage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      codingLanguages: prev.codingLanguages.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };


  const updateTech = (index, field, value) => {
    const updated = [...formData.techStackData];
    updated[index][field] = value;
    setFormData({ ...formData, techStackData: updated });
  };

  const handleRemoveTech = (index) => {
    const updated = [...formData.techStackData];
    updated.splice(index, 1);
    setFormData({ ...formData, techStackData: updated });
  };

  // Calculate total percentage
  const techStackTotal = formData.techStackData.reduce(
    (sum, t) => sum + (t.percentage || 0),
    0
  );
  const resumeEvalPercentage = formData.resumeEvaluation
    ? formData.resumeEvaluationTimePercentage
    : 0;
  const totalPercentage = techStackTotal + resumeEvalPercentage;

  return (
    <Container className="ttf-form ttf-job" maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
      <Paper className="ttf-page-header"
        elevation={1}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 2,
          textAlign: "left",
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {isClone ? "Clone Job Opening" : isEdit ? "Edit Job Opening" : "Create Job"}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {isClone
                ? "Clone this job opening and make any edits before saving"
                : isEdit
                  ? "Update your job opening description settings"
                  : "Build a new job opening to streamline your hiring process"}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained" // Change from "outlined" to "contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to="/templates"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Back to Job Openings
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className="ttf-form-scroll" sx={{ p: 4, borderRadius: "16px" }} elevation={2}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Template Details Card */}
          <StyledCard className="ttf-card ttf-card-1">
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              {/* Section Header */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    backgroundColor: "var(--primary-light)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 1.5,
                  }}
                >
                  <DescriptionIcon sx={{ color: "var(--primary)", fontSize: 20 }} />
                </Box>
                <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700, mb: 0, pb: 0, borderBottom: "none" }}>
                  Job Opening Details
                </SectionTitle>
              </Box>

              <Grid container spacing={2.5}>
                {/* Req ID (only in edit mode) */}
                {isEdit && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Req ID"
                      value={reqId}
                      disabled
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "rgba(0,0,0,0.04)",
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                )}
                {/* JobDiva Import Section */}
                <Grid className="ttf-reference-field" item xs={12} sm={6}>
                  {/* Layout (flex row, gap, field/button height) lives in RecruitmentForms.css */}
                  <Box>
                    {/* JobDiva ID Input */}
                    <TextField
                      size="small"
                      label="ATS-ID (internal / for ref)"
                      placeholder="e.g. 1234"
                      value={jobDivaRef}
                      onChange={(e) => setJobDivaRef(e.target.value)}
                    />

                    {/* JobDiva Import Button */}
                    <Button
                      className="ttf-jobdiva-import"
                      startIcon={<RecruitmentAsset name="jobDiva" />}
                      variant="outlined"
                      onClick={handleImportFromJobDiva}
                      disabled={jobDivaLoading}
                    >
                      {jobDivaLoading ? "Importing..." : "Import From JobDiva"}
                    </Button>
                  </Box>
                </Grid>
                <Grid className="ttf-title-field" item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    placeholder="Enter job title"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </Grid>

                {/* Import from URL row — now sits ABOVE the JD textarea */}
                <Grid className="ttf-url-field" item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "flex-end",
                      gap: 1,
                      p: 2,
                      borderRadius: "14px",

                      // 🎯 Premium background
                      background: "var(--bg-card)",
                      border: "1px solid var(--border-color)",
                      boxShadow: "0 4px 14px rgba(15, 118, 110, 0.08)",

                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(15, 118, 110, 0.12)",
                      },
                    }}
                  >
                    {/* Input */}
                    <TextField
                      className="ttf-url-input"
                      size="small"
                      fullWidth
                      label="Job Posting URL"
                      placeholder="https://..."
                      InputLabelProps={{ shrink: true }}
                      value={jobUrl}
                      onChange={(e) => setJobUrl(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "10px",
                          backgroundColor: "var(--bg-input)",
                        },
                      }}
                    />

                    <Box className="ttf-url-actions">
                      <Button
                        variant="contained"
                        onClick={handleFetchFromUrl}
                        disabled={urlLoading}
                        sx={{
                          height: "36px",
                          px: 2,
                          fontWeight: 700,
                          textTransform: "none",
                          borderRadius: "6px",
                          backgroundColor: "var(--primary)",

                          "&:hover": {
                            backgroundColor: "var(--primary-hover)",
                          },

                          boxShadow: "none",
                        }}
                      >
                        {urlLoading ? "Importing..." : "Import"}
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography
                        className="ttf-field-label"
                        variant="subtitle2"
                        sx={{ fontWeight: 500, color: "#37415A" }}
                      >
                        Job Description
                        {jdFileName && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "#2e7d32", fontWeight: 600, ml: 1.5 }}
                          >
                            ✓ {jdFileName}
                          </Typography>
                        )}
                      </Typography>

                    </Box>

                    <TextField
                      className="ttf-job-description ttf-job-description-field"
                      fullWidth
                      placeholder="Enter or paste job description *"
                      value={formData.jd}
                      onChange={(e) =>
                        setFormData({ ...formData, jd: e.target.value })
                      }
                      multiline
                      rows={2}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#fafafa",
                          fontSize: "0.92rem",
                        },
                      }}
                    />
                    {/* Action buttons: Upload JD + Generate Skills */}
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        justifyContent: "flex-end", // ⭐ KEY FIX
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleGenerateJD}
                        disabled={isGeneratingJD}
                        sx={{
                          height: "34px",
                          px: 2,
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "8px",
                          backgroundColor: "var(--primary)",
                        }}
                      >
                        {isGeneratingJD ? "Generating JD..." : "Ask AI to Generate JD"}
                      </Button>
                      <Button
                        component="label"
                        variant="contained"
                        size="small"
                        startIcon={<RecruitmentAsset name="upload" />}
                        disabled={jdUploading}
                        sx={{
                          height: "34px",
                          px: 2,
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "8px",

                          // 🎯 Teal style (same as Generate Skills)
                          backgroundColor: "var(--primary)",
                          color: "#fff",

                          "&:hover": {
                            backgroundColor: "#115e59",
                          },

                          "&.Mui-disabled": {
                            opacity: 0.55,
                          },
                        }}
                      >
                        {jdUploading ? "Uploading..." : "Upload JD"}

                        <VisuallyHiddenInput
                          type="file"
                          accept=".pdf,.docx,.doc"
                          onChange={handleJdFileUpload}
                          disabled={jdUploading}
                        />
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleGenerateSkills}
                        disabled={isGenerating || !formData.jd}
                        sx={{
                          height: "34px",
                          px: 2,
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "8px",
                          backgroundColor: "var(--primary)",
                          "&:hover": { backgroundColor: "var(--primary-hover)" },
                          "&.Mui-disabled": { opacity: 0.55 },
                        }}
                      >
                        {isGenerating ? "Generating..." : "Generate Skills"}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Interview Settings Card */}
          <StyledCard className="ttf-card ttf-card-2">
            <CardContent sx={{ px: 3, pt: 3, pb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                    Interview Settings
                  </SectionTitle>
                  <Typography variant="caption" color="text.secondary">
                    Configure the interview duration and mode.
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2.5} alignItems="flex-start">

              {/* LEFT COLUMN */}
<Grid item xs={12} md={6}>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mb: 1,
      flexWrap: "wrap",
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        color: "var(--text-primary)",
        lineHeight: 1.5,
      }}
    >
      Interview Duration
    </Typography>

    {/* Minutes */}
    <Typography
      component="span"
      sx={{
        fontSize: "0.78rem",
        fontWeight: 700,
        color: "#0f766e",
        backgroundColor: "#ccfbf1",
        padding: "3px 8px",
        borderRadius: "6px",
        lineHeight: 1.4,
      }}
    >
      Minutes
    </Typography>

    {/* Exclusion note */}
    <Typography
      component="span"
      sx={{
        fontSize: "0.75rem",
        fontWeight: 500,
        color: "#92400e",
        backgroundColor: "#fef3c7",
        padding: "3px 8px",
        borderRadius: "6px",
        lineHeight: 1.4,
      }}
    >
      Coding evaluations excluded
    </Typography>
  </Box>

  <TextField
    fullWidth
    required
    type="number"
    value={formData.interviewDuration}
    onChange={(e) => {
      const inputVal = e.target.value;

      if (inputVal === "") {
        setFormData({
          ...formData,
          interviewDuration: "",
        });
        return;
      }

      const val = parseInt(inputVal, 10);

      if (!isNaN(val) && val > 0) {
        setFormData({
          ...formData,
          interviewDuration: val.toString(),
        });
      }
    }}
    placeholder="e.g. 30"
    slotProps={{
      input: {
        endAdornment: (
          <InputAdornment position="end">
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "var(--text-secondary)",
              }}
            >
              min
            </Typography>
          </InputAdornment>
        ),
      },
      htmlInput: {
        min: 1,
        step: 1,
        onWheel: (e) => e.target.blur(),
      },
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        height: 54,
        borderRadius: "12px",
      },
    }}
  />
</Grid>


                {/* RIGHT COLUMN */}
                <Grid item xs={12} md={6}>

                  {/* SAME HEIGHT AS LEFT LABEL */}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: "var(--text-primary)",
                      lineHeight: 1.5,
                    }}
                  >
                    Interview Mode
                  </Typography>

                  <RadioGroup
                    value={displayedInterviewMode}
                    onChange={(e) => {
                      const selectedMode = e.target.value;

                      const followsGlobal =
                        (globalInterviewMode === "planned" &&
                          selectedMode === "PLANNED") ||
                        (globalInterviewMode === "dynamic" &&
                          selectedMode === "DYNAMIC");

                      setFormData((previous) => ({
                        ...previous,
                        plannedInterview: selectedMode === "PLANNED",
                        interviewModeOverride: followsGlobal
                          ? "GLOBAL"
                          : selectedMode,
                      }));
                    }}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                      },
                      gap: 1.25,
                    }}
                  >

                    {/* PREDEFINED */}
                    <Box
                      component="label"
                      sx={{
                        height: 72,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.25,
                        borderRadius: "12px",
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor:
                          displayedInterviewMode === "PLANNED"
                            ? "#4B1498"
                            : "var(--border-color)",
                        backgroundColor:
                          displayedInterviewMode === "PLANNED"
                            ? "rgba(75, 20, 152, 0.05)"
                            : "var(--bg-card)",
                        transition: "all 0.18s ease",

                        "&:hover": {
                          borderColor: "var(--primary)",
                        },

                        "& .MuiRadio-root": {
                          p: 0.5,
                        },

                        "& .MuiRadio-root.Mui-checked": {
                          color: "var(--primary)",
                        },
                      }}
                    >
                      <Radio
                        value="PLANNED"
                        checked={displayedInterviewMode === "PLANNED"}
                      />

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            lineHeight: 1.3,
                          }}
                        >
                          Predefined Interview
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.35,
                            color: "text.secondary",
                          }}
                        >
                          Uses predefined questions.
                        </Typography>
                      </Box>
                    </Box>


                    {/* DYNAMIC */}
                    <Box
                      component="label"
                      sx={{
                        height: 72,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.25,
                        borderRadius: "12px",
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor:
                          displayedInterviewMode === "DYNAMIC"
                            ? "#4B1498"
                            : "var(--border-color)",
                        backgroundColor:
                          displayedInterviewMode === "DYNAMIC"
                            ? "rgba(75, 20, 152, 0.05)"
                            : "var(--bg-card)",
                        transition: "all 0.18s ease",

                        "&:hover": {
                          borderColor: "var(--primary)",
                        },

                        "& .MuiRadio-root": {
                          p: 0.5,
                        },

                        "& .MuiRadio-root.Mui-checked": {
                          color: "var(--primary)",
                        },
                      }}
                    >
                      <Radio
                        value="DYNAMIC"
                        checked={displayedInterviewMode === "DYNAMIC"}
                      />

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            lineHeight: 1.3,
                          }}
                        >
                          Dynamic Interview
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.35,
                            color: "text.secondary",
                          }}
                        >
                          AI adapts questions dynamically.
                        </Typography>
                      </Box>
                    </Box>

                  </RadioGroup>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      color: "text.secondary",
                      fontSize: "0.72rem",
                      lineHeight: 1.4,
                    }}
                  >
                    The initial selection follows the Global Interview Mode. Changing it
                    creates a Job Opening-specific override.
                  </Typography>

                </Grid>

              </Grid>
            </CardContent>
          </StyledCard>

          {/* Evaluation Card */}
          <StyledCard className="ttf-card ttf-card-3"
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid var(--border-color)",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid var(--border-color)",
                }}
              >
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                  Evaluation Settings
                </SectionTitle>
                <Typography variant="caption" color="text.secondary">Select the evaluation components for this job opening</Typography>
                </Box>
              </Box>

              <Grid className="ttf-job-evaluation-grid" container spacing={2}>
                {/* Resume Evaluation */}
                <Grid className="ttf-job-evaluation-toggle ttf-job-resume-toggle" item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: "1px solid var(--border-color)",
                      background: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#fafafa",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.resumeEvaluation}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const techTotal = prev.techStackData.reduce((sum, tech) => sum + (tech.percentage || 0), 0);
                              const available = Math.max(0, 100 - techTotal);
                              return {
                                ...prev,
                                resumeEvaluation: e.target.checked,
                                // A full tech allocation must never be pushed over 100%.
                                resumeEvaluationTimePercentage: e.target.checked ? Math.min(10, available) : 0,
                              };
                            });
                          }}
                          color="primary"
                        />
                      }
                      label={<strong>Enable Resume Evaluation</strong>}
                    />

                    {formData.resumeEvaluation && (
                      <TextField
                        fullWidth
                        label="Resume Evaluation Percentage"
                        type="number"
                        value={
                          formData.resumeEvaluationTimePercentage === 0
                            ? ""
                            : formData.resumeEvaluationTimePercentage
                        }
                        onChange={(e) => {
                          setFormData((prev) => {
                            const techTotal = prev.techStackData.reduce((sum, tech) => sum + (tech.percentage || 0), 0);
                            return { ...prev, resumeEvaluationTimePercentage: clampAllocation(e.target.value, 100 - techTotal) };
                          });
                        }}
                        sx={{ mt: 2 }}
                        required
                      />
                    )}
                  </Box>
                </Grid>

                {/* Static Questions */}
                <Grid className="ttf-job-evaluation-toggle ttf-job-static-toggle" item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: "1px solid var(--border-color)",
                      background: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#fafafa",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.staticQuestionsEnabled || false}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              staticQuestionsEnabled: e.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                      label={<strong>Enable Static Questions</strong>}
                    />

                    {formData.staticQuestionsEnabled && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          mt: 2,
                          textTransform: "none",
                          borderRadius: 2,
                        }}
                        onClick={() => setOpenStaticDialog(true)}
                      >
                        Manage Questions
                      </Button>
                    )}
                  </Box>
                </Grid>

                {/* Code Evaluation */}
                <Grid className="ttf-job-evaluation-toggle ttf-job-code-toggle" item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: "1px solid var(--border-color)",
                      background: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#fafafa",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.codeEvaluation || false}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              codeEvaluation: e.target.checked,
                              codingLanguages: e.target.checked ? prev.codingLanguages : [],
                            }))
                          }
                          color="primary"
                        />
                      }
                      label={<strong>Enable Code Evaluation</strong>}
                    />

                    <Collapse in={formData.codeEvaluation} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid var(--border-color)" }}>
                        {/* Language, Level, and Add Button */}
                        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
                          {/* Language */}
                          <Grid item xs={12} sm={5}>
                            <FormControl fullWidth size="small" disabled={languagesLoading}>
                              <InputLabel>Select Language</InputLabel>
                              <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                                value={selectedLanguage}
                                label="Select Language"
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                              >
                                {languages.map((lang) => (
                                  <MenuItem key={lang} value={lang}>
                                    {lang}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Level */}
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Level</InputLabel>
                              <Select MenuProps={{ classes: { paper: "ttf-form-menu" } }}
                                value={selectedComplexity}
                                label="Select Level"
                                onChange={(e) => setSelectedComplexity(e.target.value)}
                              >
                                <MenuItem value="Basic">Basic</MenuItem>
                                <MenuItem value="Intermediate">Intermediate</MenuItem>
                                <MenuItem value="Advanced">Advanced</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Add */}
                          <Grid item xs={12} sm={3}>
                            <Button
                              fullWidth
                              variant="contained"
                              disabled={!selectedLanguage}
                              onClick={handleAddLanguage}
                              sx={{ height: 40, textTransform: "none", fontWeight: 600 }}
                            >
                              Add
                            </Button>
                          </Grid>
                        </Grid>


                        {/* Selected Languages Display */}
                        {formData.codingLanguages.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary", mb: 1.5 }}
                            >
                              Selected ({formData.codingLanguages.length})
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {formData.codingLanguages.map((item, idx) => (
                                <Box
                                  key={idx}
                                  className="ttf-job-code-language-row"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2.5,
                                    py: 1.4,
                                    borderRadius: "12px",
                                    border: "1px solid var(--border-color)",
                                    backgroundColor: "var(--bg-card)",
                                    width: "100%",
                                  }}
                                >
                                  {/* Language — LEFT ALIGNED */}
                                  <Box
                                    sx={{
                                      width: 160,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start", // 👈 THIS IS THE KEY
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        textAlign: "left",
                                      }}
                                    >
                                      {item.language.charAt(0).toUpperCase() + item.language.slice(1)}
                                    </Typography>
                                  </Box>


                                  {/* Level — CENTER (fixed width) */}
                                  <Box sx={{ width: 120, display: "flex", justifyContent: "center" }}>
                                    <Chip
                                      label={String(item.complexity || "")
                                        .toLowerCase()
                                        .replace(/^./, (c) => c.toUpperCase())}
                                      size="small"
                                      sx={{
                                        minWidth: 96,
                                        height: 30,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        borderRadius: "999px",
                                        backgroundColor:
                                          (String(item.complexity || "").toLowerCase() === "advanced" || String(item.complexity || "").toLowerCase() === "high")
                                            ? "#f44336"
                                            : (String(item.complexity || "").toLowerCase() === "intermediate" || String(item.complexity || "").toLowerCase() === "medium")
                                              ? "#ff9800"
                                              : "#4caf50",
                                        color: "#fff",
                                        ...difficultyTagStyle(item.complexity),
                                      }}
                                    />
                                  </Box>

                                  {/* Delete — FORCE RIGHT */}
                                  <Box
                                    sx={{
                                      width: 80,
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleRemoveLanguage(idx)}

                                      sx={{
                                        border: "1px solid #f44336",
                                        borderRadius: "8px",
                                        "&:hover": {
                                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : "#fdecea",
                                        },
                                      }}
                                    >
                                      <RecruitmentAsset name="remove" />
                                    </IconButton>
                                  </Box>


                                </Box>

                              ))}
                            </Box>
                          </Box>
                        )}

                      </Box>
                    </Collapse>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
          {/* Tech Stack Card */}
          <StyledCard className="ttf-card ttf-card-4">
            <CardContent sx={{ p: 3 }}>
              <Box className="ttf-tech-section-heading" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                      Tech Stack / Generated skills
                    </SectionTitle>
                    <Typography variant="caption" color="text.secondary">
                      Add the required technologies for this role.
                    </Typography>
                  </Box>
                </Box>
                <Button
                  className="ttf-tech-edit-button"
                  variant="outlined"
                  onClick={() => setOpenTechEditor(true)}
                  startIcon={<RecruitmentAsset name="edit" />}
                >
                  Edit Tech Stack
                </Button>
              </Box>

              <Box
                className="ttf-tech-add-row"
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 2,
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  label="Add Technology"
                  placeholder="Add Technology"
                  value={techInput}
                  onChange={(e) => setTechInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddTech())
                  }
                  size="small"
                  fullWidth
                />
                <Button
                  className="ttf-tech-add-button"
                  onClick={handleAddTech}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{ minWidth: "auto" }}
                >
                  Add
                </Button>
              </Box>

              {/* Tech Stack List */}
              {formData.techStackData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Technologies ({formData.techStackData.length}):
                  </Typography>
                  <Grid container spacing={1}>
                    {formData.techStackData.map((tech, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            background: theme.palette.mode === 'dark' ? 'var(--bg-input)' : "#f9fafb",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                            }}
                          >
                            {/* LEFT SIDE */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                flex: 1,
                              }}
                            >
                              {/* Tech Name */}
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  width: 140,   // 🔑 fixed width
                                }}
                              >
                                {tech.name}
                              </Typography>

                              {/* Difficulty */}
                              <Chip
                                label={tech.difficulty}
                                size="small"
                                sx={{
                                  width: 110,   // 🔑 fixed width
                                  height: 28,
                                  display: "flex",
                                  justifyContent: "center",
                                  fontWeight: 600,
                                  borderRadius: "999px",
                                  backgroundColor:
                                    tech.difficulty === "Advanced"
                                      ? "#f44336"
                                      : tech.difficulty === "Intermediate"
                                        ? "#ff9800"
                                        : "#4caf50",
                                  color: "#fff",
                                  ...difficultyTagStyle(tech.difficulty),
                                }}
                              />

                              {/* Percentage */}
                              <Chip
                                label={`${tech.percentage}%`}
                                size="small"
                                sx={{
                                  width: 70,   // 🔑 fixed width
                                  height: 28,
                                  display: "flex",
                                  justifyContent: "center",
                                  fontWeight: 600,
                                }}
                                variant="outlined"
                              />
                            </Box>

                            {/* RIGHT SIDE */}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveTech(index)}
                              sx={{
                                border: "1px solid #f44336",
                                borderRadius: "8px",
                                width: 36,
                                height: 36,
                              }}
                            >
                              <RecruitmentAsset name="remove" />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {isEdit && <Tooltip title={!isPlannedInterview ? "AI Question Bank generation is available only for Predefined Interviews." : ""}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleGenerateQuestionBank}
                    disabled={!isPlannedInterview || !isEdit || !formData.techStackData.length || questionBankLoading}
                    sx={{ mt: 2 }}
                  >
                    {questionBankLoading ? "Generating..." : "Regenerate AI Question Bank"}
                  </Button>
                </span>
              </Tooltip>}
              {questionBankOutdated && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  The AI Question Bank is outdated because interview settings changed. Regenerate it before scheduling interviews.
                </Alert>
              )}
            </CardContent>
          </StyledCard>

          {/* Evaluation Weighting Card */}
          <StyledCard className="ttf-card ttf-card-5">
            <CardContent sx={{ p: 3 }}>
              <Box className="ttf-weight-heading" sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                <SectionTitle className="ttf-section-title" variant="h6" sx={{ fontWeight: 700 }}>
                  Evaluation Weighting
                </SectionTitle>
                <Typography variant="caption" color="text.secondary">Select the evaluation components for this job opening</Typography>
                </Box>
                <Box className={`ttf-weight-badge ${totalPercentage === 100 ? 'is-valid' : 'is-invalid'}`}>{totalPercentage}%</Box>
              </Box>

              <Box className="ttf-weighting">
                {formData.resumeEvaluation && <Box className="ttf-weight-row">
                  <Box className="ttf-weight-label"><RecruitmentAsset name="resume" /><Typography variant="body2">Resume Evaluation</Typography></Box>
                  <TextField aria-label="Resume evaluation percentage" type="number" size="small"
                    value={formData.resumeEvaluationTimePercentage}
                    inputProps={{ min:0, max:100, 'aria-label':'Resume evaluation percentage' }}
                    InputProps={{ endAdornment:<InputAdornment position="end">%</InputAdornment> }}
                    onChange={(e) => setFormData((prev) => {
                      const techTotal = prev.techStackData.reduce((sum, item) => sum + (item.percentage || 0), 0);
                      return { ...prev, resumeEvaluationTimePercentage: clampAllocation(e.target.value, 100 - techTotal) };
                    })} />
                </Box>}
                {formData.techStackData.map((tech,index) => <Box className="ttf-weight-row" key={index}>
                  <Box className="ttf-weight-label"><RecruitmentAsset name="allocation" /><Typography variant="body2">{tech.name}</Typography></Box>
                  <TextField type="number" size="small" value={tech.percentage || 0}
                    inputProps={{ min:0,max:100,'aria-label':`${tech.name} evaluation percentage` }}
                    InputProps={{ endAdornment:<InputAdornment position="end">%</InputAdornment> }}
                    onChange={(e) => setFormData((prev) => {
                      const otherTechTotal = prev.techStackData.reduce(
                        (sum, item, itemIndex) => sum + (itemIndex === index ? 0 : (item.percentage || 0)), 0
                      );
                      const available = 100 - (prev.resumeEvaluation ? prev.resumeEvaluationTimePercentage : 0) - otherTechTotal;
                      const percentage = clampAllocation(e.target.value, available);
                      return { ...prev, techStackData: prev.techStackData.map((item, itemIndex) => itemIndex === index ? { ...item, percentage } : item) };
                    })} />
                </Box>)}
                <Box className={`ttf-weight-total ${totalPercentage===100 ? 'is-valid':'is-invalid'}`}>
                  <Typography variant="body2">Total Weight</Typography><strong>{totalPercentage}%</strong>
                </Box>
              </Box>

              {totalPercentage !== 100 && (
                <Alert
                  className="ttf-weight-warning"
                  severity="warning"
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 500,
                  }}
                >
                  Total allocation must be exactly 100% to submit.
                </Alert>
              )}
            </CardContent>
          </StyledCard>

          {/* Action Buttons */}
          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                } else {
                  navigate("/templates");
                }
              }}
              startIcon={<RecruitmentAsset name="cancel" />}
              disabled={loading}
              size="large"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || totalPercentage !== 100}
              startIcon={<SaveIcon />}
              size="large"
            >
              {loading
                ? "Saving..."
                : isClone
                  ? "Clone Job Opening"
                  : isEdit
                    ? "Update Job Opening"
                    : "Create Job Opening"}
            </Button>
          </Box>
        </Box>

        {/* TechStackModal for editing */}
        <TechStackModal
          open={openTechEditor}
          onClose={() => setOpenTechEditor(false)}
          techs={formData.techStackData}
          difficulties={formData.techStackData.map((t) => ({
            name: t.name,
            difficulty: t.difficulty,
          }))}
          resumeEvaluation={formData.resumeEvaluation}
          resumeEvaluationPercentage={formData.resumeEvaluationTimePercentage}
          onSave={(updatedTechs, resumeEvalPercent) => {
            setFormData((prev) => {
              const safe = normalizeAllocations(updatedTechs, prev.resumeEvaluation, resumeEvalPercent);
              return { ...prev, techStackData: safe.techs, resumeEvaluationTimePercentage: safe.resume };
            });
          }}
        />

        {/* Static Questions Dialog */}
        <Dialog className="ttf-dialog ttf-static-dialog"
          open={openStaticDialog}
          onClose={() => setOpenStaticDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box className="ttf-static-dialog-heading">
              <AssessmentIcon className="ttf-heading-icon" />
              <Box>
                <Typography component="div" className="ttf-static-dialog-title">Manage Static Questions</Typography>
                <Typography variant="body2">Add the questions candidates should answer during the interview.</Typography>
              </Box>
            </Box>
            <IconButton className="ttf-dialog-close" aria-label="Close static questions" onClick={() => setOpenStaticDialog(false)}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="New Question"
                fullWidth
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={() => {
                  if (newQuestion.trim()) {
                    setFormData((prev) => ({
                      ...prev,
                      staticQuestions: [
                        ...prev.staticQuestions,
                        newQuestion.trim(),
                      ],
                    }));
                    setNewQuestion("");
                  }
                }}
              >
                Add
              </Button>
            </Box>

            {formData.staticQuestions && formData.staticQuestions.length > 0 ? (
              <Box>
                {formData.staticQuestions.map((q, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                      p: 1,
                      border: "1px solid #ddd",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">{q}</Typography>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          staticQuestions: prev.staticQuestions.filter(
                            (_, i) => i !== idx
                          ),
                        }))
                      }
                    >
                      <RecruitmentAsset name="remove" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                <img className="ttf-static-empty" src={staticQuestionsIllustration} alt="" />No static questions added yet.
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Paper>
    </Container>
  );
};
export default TemplateForm;